// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";

interface PredicateFieldsProps {
  /** The variable/path whose fields are in scope at the cursor, e.g. "task" or "store.open_time". */
  path: string;
  /** The field names available on that object. */
  fields: readonly string[];
}

/**
 * Contextual "available fields" hint shown under the Variables row of the CEL editor. When the cursor
 * sits after a known variable path (`task.`, `store.open_time.`), it surfaces that object's fields as
 * a read-only chip row — a discoverability aid mirroring the Variables row's styling.
 */
export function PredicateFields({ path, fields }: PredicateFieldsProps) {
  const { t } = useTranslation("back-office");
  return (
    <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
      <span className="shrink-0 py-0.5">{t("octoRules.edit.fields", { variable: path })}:</span>
      <div className="flex flex-wrap gap-1.5">
        {fields.map((field) => (
          <code key={field} className="rounded bg-muted px-1.5 py-0.5 font-mono">
            {field}
          </code>
        ))}
      </div>
    </div>
  );
}
